import React from 'react'
import {
  Users,
  Star,
  DollarSign,
  Target,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  Award,
  BookOpen,
  Clock,
  Sparkles,
  BarChart3,
  Eye
} from 'lucide-react'

// 指标卡片组件
const MetricCard: React.FC<{
  title: string
  value: string
  yourValue?: string
  comparison?: 'higher' | 'lower' | 'equal'
  icon: React.ReactNode
  color: string
}> = ({ title, value, yourValue, comparison, icon, color }) => {
  const compConfig = {
    higher: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: <TrendingUp className="w-3 h-3" />, label: '高于均值' },
    lower: { bg: 'bg-rose-50', text: 'text-rose-600', icon: <TrendingDown className="w-3 h-3" />, label: '低于均值' },
    equal: { bg: 'bg-blue-50', text: 'text-blue-600', icon: <span className="text-xs">=</span>, label: '持平' }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:border-slate-200 transition-all">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-lg`}>
          {icon}
        </div>
        {comparison && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${compConfig[comparison].bg} ${compConfig[comparison].text}`}>
            {compConfig[comparison].icon}
            {compConfig[comparison].label}
          </div>
        )}
      </div>
      <div className="text-sm text-slate-500 mb-1">{title}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {yourValue && (
        <div className="mt-2 text-sm text-slate-500">
          您的数据: <span className="font-semibold text-indigo-600">{yourValue}</span>
        </div>
      )}
    </div>
  )
}

// 竞争者卡片组件
const CompetitorCard: React.FC<{
  rank: number
  name: string
  rating: number
  courseCount: number
  price: number
  studentCount: number
}> = ({ rank, name, rating, courseCount, price, studentCount }) => (
  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:bg-white hover:shadow-md transition-all">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white ${
      rank === 1 ? 'bg-amber-400' : rank === 2 ? 'bg-slate-400' : rank === 3 ? 'bg-orange-400' : 'bg-slate-300'
    }`}>
      {rank}
    </div>
    <div className="flex-1">
      <div className="font-semibold text-slate-800">{name}</div>
      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
        <span className="flex items-center gap-1">
          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
          {rating}
        </span>
        <span>{courseCount} 门课</span>
        <span>{studentCount} 学生</span>
      </div>
    </div>
    <div className="text-right">
      <div className="text-lg font-bold text-indigo-600">¥{price}</div>
      <div className="text-xs text-slate-500">/课时</div>
    </div>
  </div>
)

export const TeacherCompetitorAnalysis: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* 头部 */}
      <div className="relative rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAxOGMtMy4zMTQgMC02LTIuNjg2LTYtNnMyLjY4Ni02IDYtNiA2IDIuNjg2IDYgNi0yLjY4NiA2LTYgNnoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-50" />
        <div className="relative px-8 py-10 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            <Eye className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">竞争对手分析</h2>
          <p className="text-slate-400">分析竞争对手情况，制定差异化策略</p>
        </div>
      </div>

      {/* 市场概览 */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <BarChart3 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">同领域教师概览</h3>
            <p className="text-sm text-slate-500">基于您的课程类别分析</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="市场平均价格"
            value="¥250/课时"
            yourValue="¥280/课时"
            comparison="higher"
            icon={<DollarSign className="w-5 h-5" />}
            color="from-emerald-400 to-teal-500"
          />
          <MetricCard
            title="平均评分"
            value="4.6 分"
            yourValue="4.8 分"
            comparison="higher"
            icon={<Star className="w-5 h-5" />}
            color="from-amber-400 to-orange-500"
          />
          <MetricCard
            title="平均课程数"
            value="12 门"
            yourValue="8 门"
            comparison="lower"
            icon={<BookOpen className="w-5 h-5" />}
            color="from-blue-400 to-indigo-500"
          />
          <MetricCard
            title="平均教龄"
            value="3.2 年"
            yourValue="4 年"
            comparison="higher"
            icon={<Clock className="w-5 h-5" />}
            color="from-purple-400 to-pink-500"
          />
        </div>
      </div>

      {/* 竞争者排行 */}
      <div className="bg-white rounded-3xl border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">同类教师排行</h3>
            <p className="text-sm text-slate-500">您当前排名第 <span className="text-indigo-600 font-bold">5</span> 位</p>
          </div>
        </div>

        <div className="space-y-3">
          <CompetitorCard rank={1} name="张老师" rating={4.9} courseCount={18} price={320} studentCount={256} />
          <CompetitorCard rank={2} name="王老师" rating={4.8} courseCount={15} price={290} studentCount={198} />
          <CompetitorCard rank={3} name="李老师" rating={4.8} courseCount={12} price={270} studentCount={167} />
          <div className="p-4 bg-indigo-50 rounded-xl border-2 border-indigo-200">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center font-bold text-white">
                5
              </div>
              <div className="flex-1">
                <div className="font-semibold text-indigo-800">您的位置</div>
                <div className="flex items-center gap-3 text-xs text-indigo-600 mt-1">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    4.8
                  </span>
                  <span>8 门课</span>
                  <span>89 学生</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-indigo-600">¥280</div>
                <div className="text-xs text-indigo-500">/课时</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI 建议 */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl border border-indigo-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-lg">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">AI 差异化建议</h3>
            <p className="text-sm text-slate-500">帮助您脱颖而出</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            { text: '增加 4 门课程可进入前 3 名', icon: <BookOpen className="w-4 h-4" /> },
            { text: '提升完课率可增加 15% 学生量', icon: <TrendingUp className="w-4 h-4" /> },
            { text: '添加试听课程可提高转化率', icon: <Users className="w-4 h-4" /> },
            { text: '优化课程介绍提升点击率 20%', icon: <Target className="w-4 h-4" /> }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-indigo-100">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600">
                {item.icon}
              </div>
              <span className="text-sm text-slate-700">{item.text}</span>
              <ArrowUpRight className="w-4 h-4 text-indigo-400 ml-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
